import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { LockOutlined, RightOutlined } from '@ant-design/icons';
import { Col, List, Row, Space, Switch, Typography } from 'antd';
import { useUIDispatchContext } from 'providers/UI';
import { useTranslation } from 'react-i18next';
import {
  NotificationKeys,
  NOTIFICATION_VALUES,
  SettingsKeys,
  checkPermission,
  parseJson
} from '@medicusunion/front-utils';
import { useAuthDispatchContext, useAuthStateContext } from 'providers/Auth';
import { updateSettings } from 'services/api/profile';

import PasswordModal from '../Modals/PasswordModal';
import Section from '../Section';
import { showRestError } from 'utils/showErrorMessage';
import Auth2FA from '../Auth2FA';

const { Text } = Typography;

const DATA = [
  {
    title: 'your_password',
    icon: <LockOutlined />
  }
];

const Settings: FC = () => {
  const { t } = useTranslation();
  const { toggleModal } = useUIDispatchContext();
  const { refetchUserData } = useAuthDispatchContext();
  const { user } = useAuthStateContext();
  const shouldUpdate = useRef(false);

  const userNotificationSetting = useMemo(() => {
    const value = parseJson(user?.settings)?.[SettingsKeys.notifications];

    return typeof value === 'number' ? value : 0;
  }, [user?.settings]);

  const [result, setResult] = useState<number>(userNotificationSetting);

  useEffect(() => {
    if (shouldUpdate.current) {
      // settings is merged server-side (auth2fa preserved) — send only
      // the changed key
      updateSettings({ [SettingsKeys.notifications]: result })
        .then(() => refetchUserData())
        .catch(err => showRestError(err));
    }
  }, [refetchUserData, result]);

  const onSettingSwitch = useCallback(
    (key: NotificationKeys, value: boolean) => {
      shouldUpdate.current = true;
      const num = NOTIFICATION_VALUES[key];

      setResult(prev => prev + (value ? num : -num));
    },
    []
  );

  return (
    <>
      <Space size={16} direction="vertical" style={{ width: '100%' }}>
        <Section title={t('profile.alerts')}>
          <Space size={16} direction="vertical">
            <Space size={8} align="center">
              <Switch
                checked={checkPermission(
                  NOTIFICATION_VALUES[NotificationKeys.sms],
                  result
                )}
                onChange={value => onSettingSwitch(NotificationKeys.sms, value)}
              />
              <Text>{t('profile.sms_notifications')}</Text>
            </Space>
            <Space size={8} align="center">
              <Switch
                checked={checkPermission(
                  NOTIFICATION_VALUES[NotificationKeys.email],
                  result
                )}
                onChange={value =>
                  onSettingSwitch(NotificationKeys.email, value)
                }
              />
              <Text>{t('profile.email_notifications')}</Text>
            </Space>
            <Space size={8} align="center">
              <Switch
                checked={checkPermission(
                  NOTIFICATION_VALUES[NotificationKeys.push],
                  result
                )}
                onChange={value =>
                  onSettingSwitch(NotificationKeys.push, value)
                }
              />
              <Text>{t('profile.push_notifications')}</Text>
            </Space>
          </Space>
        </Section>
        <Section title={t('profile.privacy')}>
          <List
            bordered
            dataSource={DATA}
            renderItem={item => (
              <List.Item
                style={{ cursor: 'pointer' }}
                onClick={() => toggleModal('password', true)}
              >
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ width: '100%' }}
                >
                  <Space size={8}>
                    {item.icon}
                    <Text>{t(`profile.${item.title}`)}</Text>
                  </Space>
                  <Col>
                    <RightOutlined />
                  </Col>
                </Row>
              </List.Item>
            )}
          />
          <Auth2FA />
        </Section>
      </Space>
      <PasswordModal />
    </>
  );
};

export default memo(Settings);
